/**
 * Sharing between devices (a tablet and a phone, or two tablets)
 * ---------------------------------------------------------------
 * Every device keeps its full copy and works offline. A small store on the same free
 * Cloudflare account as the app ("the share") passes changes between them:
 *
 *  push  the device sends records it changed, each with the share version it last saw
 *        ("base"). The share accepts it when nobody changed it in between; otherwise it
 *        answers with its copy and the device merges field by field (both edits kept when they
 *        touch different fields — e.g. two people finishing two different areas) and sends again.
 *  pull  the device asks for everything with a sequence number above the last one it has.
 *
 * A group of devices shares one secret key (made on the first device; other devices get it
 * with a one-time 8-letter join code). The share only ever sees records, never the key itself
 * (it stores a hash of it).
 */

/** The tables that are shared. Each record has a string `id`. */
export const SYNCED_TABLES = ['settings', 'people', 'items', 'nights', 'counts', 'moves', 'events', 'audit', 'feedback'] as const;
/**
 * Goes up by one whenever a table is added above. A device on an older version skips records
 * of tables it doesn't know, so after updating it fetches everything once more to get them.
 */
export const TABLES_VERSION = 2;
export type SyncedTable = (typeof SYNCED_TABLES)[number];
export const isSyncedTable = (t: string): t is SyncedTable => (SYNCED_TABLES as readonly string[]).includes(t);

export interface ChangeIn {
  tbl: SyncedTable;
  id: string;
  /** The share's version this change is based on; null for a record the device thinks is new. */
  base: number | null;
  /** The whole record, or null when it was deleted. */
  data: Record<string, unknown> | null;
}

export interface ServerRecord {
  tbl: SyncedTable;
  id: string;
  v: number;
  seq: number;
  data: Record<string, unknown> | null;
}

export type ChangeResult = { ok: true; v: number; seq: number } | { ok: false; current: ServerRecord | null };

export interface PushResponse {
  results: ChangeResult[];
}

export interface PullResponse {
  records: ServerRecord[];
  /** Pass back as `since` next time. */
  next: number;
  more: boolean;
}

export const MAX_PUSH = 200;
export const MAX_PULL = 500;
export const MAX_RECORD_BYTES = 200_000;
export const PAIR_MINUTES = 15;
/** Join codes: 8 letters/digits without look-alikes (no 0/O, 1/I/L). */
export const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * A group's key: 40 letters and digits (about 195 bits), easy to read out and type, so it
 * doubles as the owner's recovery code.
 */
export function randomKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(40));
  return [...bytes].map((b) => CODE_ALPHABET[b % CODE_ALPHABET.length]).join('');
}

/** "ABCDE-FGHJK-…" for writing down. */
export const formatKey = (key: string) => key.match(/.{1,5}/g)?.join('-') ?? key;

export function randomCode(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return [...bytes].map((b) => CODE_ALPHABET[b % CODE_ALPHABET.length]).join('');
}

/** "k7qf 3mzd" → "K7QF3MZD". */
export const normalizeCode = (code: string) => code.toUpperCase().replace(/[^A-Z0-9]/g, '');
export const formatCode = (code: string) => `${code.slice(0, 4)}-${code.slice(4)}`;
